import {
  ArchetypeDiscovery,
  DeltaViewType,
  DimensionEvent,
  DimensionEventStatus,
  DimensionOutroSummary,
  ElementDiscovery,
  Item,
  ItemResponse,
  PatternScoreHistory,
  RankOrderItemResponse,
  RankOrderItemSet,
  RankOrderItemSetResponse,
  ScaleScoreHistory,
  ScheduledAssessment,
  TraitIdentifier,
} from "@dimensional-engineering/dimensional-models";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { User } from "firebase/auth";

import elementsJsonData from "../../assets/traitInfo/elements.json";

import { db } from "@/_firebase/firebaseConfig";
import { RankOrderItemResponseWithStep } from "./views/RankListAndRegularItemAssessmentView/RankListAndRegularItemAssessmentView";
import { getUserScaleScore } from "../traits/utils";

export async function handleAssessmentStartDimensionRefUpdate(
  slug: string,
  uid: string
) {
  const dimensionEventRef = doc(
    db,
    "members",
    `${uid}`,
    "dimensionEvents",
    slug as string
  );
  const payload: DimensionEvent = {
    //@ts-ignore
    createdOn: Date.now(),
    dimensionSlug: slug,
    event: DimensionEventStatus.started,
    progress: 0,
  };
  return await setDoc(dimensionEventRef, payload);
}

export async function getResponses(slug: string, uid: string | undefined) {
  const responseRef = collection(db, `members/${uid}/itemResponses`);
  const q = query(responseRef, where("dimensionSlug", "==", slug));
  const querySnapshot = await getDocs(q);
  let sortedAnswers: ItemResponse[] = [];
  querySnapshot.docs.forEach((doc) => {
    sortedAnswers.push(doc.data() as ItemResponse);
  });
  return sortedAnswers;
}

export async function getAllItems(slug: string) {
  const itermRef = collection(db, "items");
  const q = query(itermRef, where("dimensionSlug", "==", slug));
  const sortedItems: Item[] = [];
  await getDocs(q).then((querySnapshot) => {
    querySnapshot.docs.forEach((doc) => {
      sortedItems.push(doc.data() as Item);
    });
  });
  sortedItems.sort((a, b) => {
    if (a.assessmentOrder && b.assessmentOrder) {
      return a.assessmentOrder - b.assessmentOrder;
    } else {
      return 0;
    }
  });
  return sortedItems;
}

export async function getRankOrderItems(slug: string) {
  const ref = collection(db, "rankOrderItemSets");
  const q = query(ref, where("dimensionSlug", "==", slug));
  let rankorderItem: RankOrderItemSet | undefined;
  await getDocs(q).then((querySnapshot) => {
    querySnapshot.docs.forEach((doc) => {
      rankorderItem = doc.data() as RankOrderItemSet;
    });
  });

  return rankorderItem;
}

export async function getRankOrderResponses(
  slug: string,
  uid: string | undefined
) {
  const responseRef = doc(
    db,
    `members/${uid}/rankOrderItemSetResponses/${
      rankListSlugMap[slug as keyof typeof rankListSlugMap]
    }`
  );
  const docSnap = await getDoc(responseRef);

  if (docSnap.exists()) {
    const data = docSnap.data() as RankOrderItemSetResponse;
    return data;
  } else {
    return undefined;
  }
}

export function handleTutorialsExperience(
  elements: ElementDiscovery[] | null,
  patternsLength: number | undefined
) {
  const elementsLocalStorageTutorial = localStorage.getItem("elementsTutorial");
  const patternsLocalStorageTutorial = localStorage.getItem("patternsTutorial");

  let inTutorial = false;
  let tutorialType: "element" | "pattern" = "element";
  if (elementsLocalStorageTutorial) {
    if (
      elements?.length &&
      elements?.length > 0 &&
      elementsLocalStorageTutorial === "true"
    ) {
      inTutorial = true;
      tutorialType = "element";
    }
  }
  if (patternsLocalStorageTutorial) {
    if (patternsLength === 1 && patternsLocalStorageTutorial === "true") {
      inTutorial = true;
      tutorialType = "pattern";
    }
  }
  return {
    inTutorial: inTutorial,
    tutorialType: tutorialType,
  };
}

export async function handleItemAnswer({
  value,
  text,
  item,
  slug,
  uid,
  currentStep,
  items,
}: {
  text: string;
  value: number;
  item: Item;
  slug: string;
  uid: string | undefined;
  currentStep: number;
  items: Item[];
}) {
  const payload: ItemResponse = {
    createdOn: Date.now(),
    dimensionSlug: slug as string,
    itemId: item.slug,
    itemText: item.text,
    scaleSlugs: item.scaleSlugs,
    selectedOption: {
      text: text,
      value: value,
    },
  };

  const answerRef = doc(
    db,
    "members",
    `${uid}`,
    "itemResponses",
    `${item.slug}`
  );

  const dimensionEventRef = doc(
    db,
    "members",
    `${uid}`,
    "dimensionEvents",
    slug as string
  );

  updateDoc(dimensionEventRef, {
    progress: currentStep / items.length,
    updatedOn: Date.now(),
  });

  return await setDoc(answerRef, payload);
}

export function handleItemAnswerScroll() {
  const el = document.getElementById("carouselContainer");
  const main = document.getElementById("mainAssessment");
  if (el && main) {
    main.style.overflow = "hidden";
    el.style.transition = "opacity 0.2s, top 0.2s"; // Add CSS transitions for opacity and top properties

    el.style.opacity = "0"; // Fade out the element

    setTimeout(() => {
      el.style.top = "-500px"; // Move the element up
    }, 50);

    setTimeout(() => {
      el.style.transition = "top 0.2s"; // Add a transition for the top property only
      el.style.top = "500px"; // Move the element down
    }, 100);

    setTimeout(() => {
      el.style.top = "0"; // Scroll the element back to its original position
      el.style.opacity = "1"; // Fade in the element
    }, 200);

    // setTimeout(() => {
    //   main.style.overflow = "auto";
    // }, 300);
  }
}

export function handleItemAnswerUndoScroll() {
  const el = document.getElementById("carouselContainer");
  const main = document.getElementById("mainAssessment");
  if (el && main) {
    main.style.overflow = "hidden";
    el.style.transition = "top 0.2s"; // Add CSS transitions for opacity and top properties

    el.style.opacity = "0"; // Fade out the element

    setTimeout(() => {
      el.style.top = "500px"; // Move the element up
    }, 50);

    setTimeout(() => {
      el.style.transition = "top 0.2s"; // Add a transition for the top property only
      el.style.top = "-500px"; // Move the element down
    }, 100);

    setTimeout(() => {
      el.style.top = "0"; // Scroll the element back to its original position
      el.style.opacity = "1"; // Fade in the element
      // main.style.overflow = "auto";
    }, 200);
  }
}

export async function checkIfAllAnswersAreDone(
  items: Item[],
  addUnansweredItems: (item: Item) => void,
  user: User | null | undefined
) {
  let answeredFinished = true;

  await Promise.all(
    items.map(async (item) => {
      const ref = doc(
        db,
        "members",
        `${user?.uid}`,
        "itemResponses",
        `${item.slug}`
      );

      const docSnapshot = await getDoc(ref);
      if (!docSnapshot.exists()) {
        answeredFinished = false;
        addUnansweredItems(item);
      }
    })
  );

  return answeredFinished;
}

export function generateItemResponse(
  slug: string,
  item: Item,
  text: string,
  value: number
) {
  const payload: ItemResponse = {
    createdOn: Date.now(),
    dimensionSlug: slug as string,
    itemId: item.slug,
    itemText: item.text,
    scaleSlugs: item.scaleSlugs,
    selectedOption: {
      text: text,
      value: value,
    },
  };

  return payload;
}

export function sanitizeRankOrderItemResponses(
  items: RankOrderItemResponseWithStep[]
): RankOrderItemResponse[] {
  return items.map(({ step, ...rest }) => rest);
}

export async function setRankOrderResponsesToDB(
  items: RankOrderItemResponse[],
  slug: string,
  uid: string | undefined
) {
  const answerRef = doc(
    db,
    "members",
    `${uid}`,
    "rankOrderItemSetResponses",
    rankListSlugMap[slug as keyof typeof rankListSlugMap]
  );

  const payload: RankOrderItemSetResponse = {
    slug: rankListSlugMap[slug as keyof typeof rankListSlugMap],
    dimensionSlug: slug,
    responses: items,
  };

  return await setDoc(answerRef, payload);
}

export function createRankOrderResponseSetPayload(
  items: RankOrderItemResponse[],
  slug: string
): RankOrderItemSetResponse {
  const rankOrderPayload: RankOrderItemSetResponse = {
    slug: rankListSlugMap[slug as keyof typeof rankListSlugMap],
    dimensionSlug: slug,
    responses: items,
  };

  return rankOrderPayload;
}

export function sortDiscoveredElements(elements: ElementDiscovery[]) {
  return [...elements].sort((a: ElementDiscovery, b: ElementDiscovery) => {
    if (a?.createdOn && b?.createdOn) {
      return a?.createdOn - b?.createdOn;
    } else {
      return 0;
    }
  });
}

export async function getDimensionEventData(
  slug: string | undefined,
  userId: string | undefined
) {
  ///getting initial status on the assessment
  let dimensionQuery: any;
  if (slug && userId) {
    dimensionQuery = doc(
      db,
      "members",
      `${userId}`,
      "dimensionEvents",
      slug as string
    );
  }

  const docSnap = await getDoc(dimensionQuery);

  let retake = false;
  let status: "notStarted" | "started" | "complete" = "notStarted";
  let progress = 0;
  if (docSnap.exists()) {
    const data = docSnap.data() as DimensionEvent;
    if (data.event) {
      if (
        data.event === DimensionEventStatus.started ||
        data.event === DimensionEventStatus.reset ||
        data.event === DimensionEventStatus.resetGated
      ) {
        status = "started";
        retake = true;
      } else {
        status = "complete";
      }
      if (data.progress) {
        progress = data.progress;
      } else {
        progress = 0;
      }
    } else {
      status = "notStarted";
    }
  } else {
    status = "notStarted";
  }

  return { retake: retake, status: status, progress: progress };
}

export const removeDuplicates = (items: ItemResponse[]): ItemResponse[] => {
  const uniqueItems: Record<string, ItemResponse> = {};

  items.forEach((item) => {
    uniqueItems[item.itemId] = item;
  });

  const uniqueItemsArray = Object.values(uniqueItems);

  return uniqueItemsArray;
};

export const showHeaderMap: { [key: number]: boolean } = {
  1: true,
  2: true,
  3: true,
  4: false,
  5: true,
  6: false,
  7: false,
  8: false,
  9: false,
  10: false,
  11: false,
  12: false,
  13: false,
  14: true,
  15: true,
  16: false,
  17: true,
  18: false,
};

export const backButtonEnabledMap: { [key: number]: boolean } = {
  1: true,
  2: true,
  3: true,
  4: false,
  5: false,
  6: false,
  7: false,
  8: false,
  9: false,
  10: false,
  11: false,
  12: false,
  13: false,
  14: false,
  15: true,
  16: false,
  17: false,
  18: false,
};

export const headerTitlesMap: { [key: number]: string } = {
  1: "Phone number",
  2: "Verify number",
  3: "Username",
  4: "",
  5: "Welcome",
  6: "Cognition",
  7: "",
  8: "",
  9: "",
  10: "",
  11: "",
  12: "",
  13: "",
  14: "",
  15: "",
  16: "",
  17: "Find friends on Dimensional",
  18: "",
};

export const dichotomySlugs = [
  "conflict-style",
  "personality-II",
  "cognition-long",
];
export const coreAssessmentSlugs = ["personality", "personality-II", "values"];
export const rankListSlugMap = {
  values: "value-in-life",
  personality: "primary-traits-stand-out",
  strengths: "strengths-most-define-you",
};

export function filterValuesArchetypeFromDimensionOutro(
  dimensionOutroSummary: DimensionOutroSummary
): DimensionOutroSummary {
  return {
    ...dimensionOutroSummary,
    archetypeDiscoveries: dimensionOutroSummary.archetypeDiscoveries.filter(
      (discovery) => discovery.archetype.archetypeGroupSlug !== "values"
    ),
  };
}

export function checkIfBasisOfAllDimensionsComplete(
  userDimensionEvents: DimensionEvent[] | null
): boolean {
  const allDimensionSlugs = [
    "personality-II",
    "values",
    "personality",
    "communication-style",
    "love-style",
    "love-attitudes",
    "attachment-style",
    "interests",
    "strengths",
    "conflict-style",
    "political-ideology",
    "secondary-traits",
    "lifestyle",
  ];

  if (!userDimensionEvents) {
    return false;
  } else {
    for (let i = 0; i < allDimensionSlugs.length; i++) {
      const slug = allDimensionSlugs[i];
      const foundEvent = userDimensionEvents.find(
        (e) => e.dimensionSlug === slug
      );
      if (!foundEvent || foundEvent.event === DimensionEventStatus.started) {
        return false;
      }
    }
  }

  return true;
}

export function createStepsForOutro(
  outro: DimensionOutroSummary | null,
  archetypeDiscoveries: ArchetypeDiscovery[] | null
): Record<number, string> {
  if (!outro) {
    return {};
  }

  const outroObject: string[] = [];

  const deltaViews = outro.dimensionSummary?.deltaViews;
  const foundNatureDelta = deltaViews?.find(
    (d) => d.deltaViewType === DeltaViewType.natureDelta
  );
  const foundArchetypeDelta = deltaViews?.find(
    (d) => d.deltaViewType === DeltaViewType.archetypeDelta
  );
  const foundTopNDelta = deltaViews?.find(
    (d) => d.deltaViewType === DeltaViewType.topNDelta
  );
  const foundTopTraitDelta = deltaViews?.find(
    (d) => d.deltaViewType === DeltaViewType.topTraitDelta
  );
  const foundTwoByTwoDelta = deltaViews?.find(
    (d) => d.deltaViewType === DeltaViewType.twoByTwoDelta
  );
  const foundSpectrumDelta = deltaViews?.find(
    (d) => d.deltaViewType === DeltaViewType.spectrumDelta
  );

  if (foundNatureDelta) {
    //show delta
    outroObject.push("NatureDiscoveryDeltaView");
  } else if (outro.primaryNature) {
    outroObject.push("NatureDiscoveryStepOne");
  }

  if (foundArchetypeDelta) {
    outroObject.push("ArchetypeDiscoveryScreenDeltaView");
  } else if (outro.archetypeDiscoveries) {
    outro.archetypeDiscoveries.forEach((d) => {
      outroObject.push("ArchetypeDiscoveryScreenStepOne");
      outroObject.push("ArchetypeDiscoveryScreenStepTwo");
      if (archetypeDiscoveries?.length === 1) {
        outroObject.push("ArchetypeDiscoveryStepThree");
      }
    });
  }

  if (foundTopNDelta) {
    outroObject.push("TopTraitsDeltaView");
  }

  if (foundTwoByTwoDelta) {
    outroObject.push("TwoByTwoDeltaView");
  }

  if (foundSpectrumDelta) {
    outroObject.push("SpectrumsDeltaView");
  }

  if (foundTopTraitDelta) {
    //check for 2 scores in history
    outroObject.push("SingleTraitDeltaView");
  } else {
    //there is not 2 by 2 delta or specrtum delta
    if(!foundTwoByTwoDelta){
      outroObject.push("DiscoveredElements");
      outroObject.push("SignatureDiscoveredElements");
    }
  }

  const outroRecord: Record<number, string> = {};

  outroObject.forEach((value, index) => {
    outroRecord[index] = value;
  });

  return outroRecord;
}

export function determineIfTopNDeltaHasChanged(
  previousTraitIdentifiers: TraitIdentifier[] | undefined,
  currentTraitIdentifiers: TraitIdentifier[] | undefined
): boolean {
  const prevMap: Record<number, string> = {};

  if (!previousTraitIdentifiers || !currentTraitIdentifiers) {
    return false;
  }

  previousTraitIdentifiers.forEach((t, i) => {
    prevMap[i] = t.slug;
  });

  for (let i = 0; i < currentTraitIdentifiers.length; i++) {
    const current = currentTraitIdentifiers[i];
    if (prevMap[i] !== current.slug) {
      return true;
    }
  }

  return false;
}

export function getPreviousPercentScoreFromScoreHistory(
  scoreHistory: ScaleScoreHistory | null | undefined
): number {
  const scores = scoreHistory?.historicScores;

  if (scores?.length) {
    const index = scores.length >= 2 ? scores.length - 2 : scores.length - 1;
    return scores[index]?.percentScore ?? 0;
  }

  return 0;
}

export function getPreviousPercentScoreFromScoreHistoryPatterns(
  scoreHistory: PatternScoreHistory | null | undefined
): number {
  const scores = scoreHistory?.historicScores;

  if (scores?.length) {
    const index = scores.length >= 2 ? scores.length - 2 : scores.length - 1;
    return scores[index]?.percentScore ?? 0;
  }

  return 0;
}

export function getPreviousDimensionSlugSource(
  scoreHistory: ScaleScoreHistory | null | undefined
): string | null | undefined {
  const scores = scoreHistory?.historicScores;

  if (scores?.length) {
    const index = scores.length >= 2 ? scores.length - 2 : scores.length - 1;
    return scores[index]?.dimensionSlugSource;
  }

  return null;
}

export function getPreviousIsLongFormGenerated(
  scoreHistory: PatternScoreHistory | null | undefined
): boolean | null | undefined {
  const scores = scoreHistory?.historicScores;

  if (scores?.length) {
    const index = scores.length >= 2 ? scores.length - 2 : scores.length - 1;
    return scores[index]?.isLongFormGenerated;
  }

  return null;
}

export function getLengthOfLongestArchetypeName(
  archOne: string | undefined,
  archTwo: string | undefined
) {
  return Math.max(archOne?.length ?? 0, archTwo?.length ?? 0);
}

export function sortByScoreThenNameForOutroBreakdown(
  a: ElementDiscovery,
  b: ElementDiscovery
) {
  const foundElementA = elementsJsonData.find((e) => e.slug === a.scaleSlug);
  const foundElementB = elementsJsonData.find((e) => e.slug === b.scaleSlug);

  const scoreA =
    getUserScaleScore(
      a.scaleScore?.percentScore,
      a.scaleSlug,
      a.scaleScore?.dimensionSlugSource
    ) ?? 0;
  const scoreB =
    getUserScaleScore(
      b.scaleScore?.percentScore,
      b.scaleSlug,
      b.scaleScore?.dimensionSlugSource
    ) ?? 0;

  if (scoreB !== scoreA) {
    return scoreB - scoreA;
  }

  const nameA = (
    foundElementA?.alias ??
    foundElementA?.name ??
    ""
  ).toLowerCase();
  const nameB = (
    foundElementB?.alias ??
    foundElementB?.name ??
    ""
  ).toLowerCase();

  return nameA.localeCompare(nameB);
}

export function isAssessmentWithin30DayWindowOfCurrentShceduledAssessment(
  slug: string,
  scheduledAssessments: ScheduledAssessment[]
): boolean {
  const found = scheduledAssessments.find((a) => a.assessmentSlug === slug);
  const lastOccurrence = found?.lastOccurrence;

  if (!lastOccurrence) return false;

  const now = Timestamp.now();
  const thirtyDaysAgo = Timestamp.fromMillis(
    now.toMillis() - 30 * 24 * 60 * 60 * 1000
  );
  const isWithinWindow = lastOccurrence.toMillis() >= thirtyDaysAgo.toMillis();

  return isWithinWindow;
}
